import React from 'react';
import { FieldGroup } from '@puckeditor/core';
import type { FieldGroupVariant } from '@puckeditor/core';

export type FieldGroupDefinition<Key extends string> = {
  label: string;
  variant?: FieldGroupVariant;
  /**
   * Initial open state. Top-level groups start collapsed except the first rendered one (the block's
   * primary group); nested cards start open so a parent that is opened shows its contents.
   */
  defaultExpanded?: boolean;
  fields: readonly (FieldGroupDefinition<Key> | Key)[];
};

/** Presentation only: consume Puck's resolved field elements, never recreate fields from config.
 * Nested definitions are cards; absent fields and empty groups are omitted. Leaving Puck's group
 * id unset scopes expansion state to this mounted inspector rather than sharing it across blocks.
 */
export function renderFieldGroups<Key extends string>(
  fields: Partial<Record<Key, React.ReactNode>>,
  groups: readonly FieldGroupDefinition<Key>[]
): JSX.Element {
  let primaryAssigned = false;
  const renderGroup = (group: FieldGroupDefinition<Key>, nested = false): React.ReactElement | null => {
    const children = group.fields.flatMap((entry) => {
      if (typeof entry !== 'string') {
        const child = renderGroup(entry, true);
        return child ? [child] : [];
      }
      const field = fields[entry];
      return field == null ? [] : [<FieldGroup.Item key={entry}>{field}</FieldGroup.Item>];
    });
    if (!children.length) return null;
    const isPrimary = !nested && !primaryAssigned;
    if (!nested) primaryAssigned = true;
    // Test hook: installed-Puck contracts set this global so every group starts open; unset in production.
    const expandAll =
      (globalThis as { __CMS_EXPAND_ALL_FIELD_GROUPS__?: boolean }).__CMS_EXPAND_ALL_FIELD_GROUPS__ === true;
    const defaultExpanded = expandAll || (group.defaultExpanded ?? (nested || isPrimary));
    return (
      <FieldGroup
        key={group.label}
        label={group.label}
        variant={group.variant ?? (nested ? 'card' : 'flush')}
        collapsible
        defaultExpanded={defaultExpanded}
      >
        {children}
      </FieldGroup>
    );
  };
  return <>{groups.map((group) => renderGroup(group))}</>;
}
